import { Link } from "@tanstack/react-router";
import { Megaphone, Sparkles, Trophy, Users, Film } from "lucide-react";
import { HypeClipComposer } from "./HypeClipComposer";
import { ViralMomentSchedule } from "./ViralMomentSchedule";
import { SponsorMediaKit } from "./SponsorMediaKit";
import { MOCK_VIRAL_CLIPS, MOCK_MEDIA_BLITZ_TOTALS } from "@/lib/demo/executiveDemoMock";

/**
 * MediaBlitzHub — centralizes everything creators / ops need to distribute
 * VerseSport during the World Cup: clip composer, share shortcuts, viral
 * moment timeline and sponsor-ready metrics. Pure UI composition.
 */
export function MediaBlitzHub() {
  const SHORTCUTS = [
    { to: "/showcase", icon: Sparkles, label: "Showcase", hint: "Demo cinematográfico" },
    { to: "/creator-studio", icon: Megaphone, label: "Creator Studio", hint: "Clips + ranking + links" },
    { to: "/ambassadors", icon: Trophy, label: "Ambassadors", hint: "Misiones + crews" },
    { to: "/wc/$slug", params: { slug: "worldcup" }, icon: Users, label: "Landing global", hint: "/wc/worldcup" },
  ] as const;

  return (
    <div className="space-y-4">
      <section className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {SHORTCUTS.map((s) => {
          const Icon = s.icon;
          const node = (
            <div className="rounded-2xl border border-border/60 bg-background/70 p-2.5 active:scale-[0.98]">
              <div className="flex items-center gap-1.5">
                <Icon className="h-3.5 w-3.5 text-primary" />
                <span className="text-[11px] font-black uppercase tracking-widest">{s.label}</span>
              </div>
              <div className="mt-0.5 text-[10px] text-muted-foreground">{s.hint}</div>
            </div>
          );
          return "params" in s ? (
            <Link key={s.label} to={s.to} params={s.params}>{node}</Link>
          ) : (
            <Link key={s.label} to={s.to}>{node}</Link>
          );
        })}
      </section>

      <HypeClipComposer />
      <ViralMomentSchedule />
      <SponsorMediaKit />
      <EditorialClipsLibrary />
    </div>
  );
}

/**
 * WORLD-CUP-LAUNCH-P2 — Editorial / historical fallback library.
 * Always rendered so the Media Blitz surface is never empty before the
 * Mundial generates real viral_clips rows. Clearly labeled "editorial".
 */
function EditorialClipsLibrary() {
  const clips = MOCK_VIRAL_CLIPS.slice(0, 12);
  return (
    <section
      aria-label="Biblioteca editorial"
      className="rounded-2xl border border-border/60 bg-background/70 p-3"
    >
      <header className="mb-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <Film className="h-3.5 w-3.5 text-primary" />
          <h3 className="text-[11px] font-black uppercase tracking-widest">Biblioteca editorial</h3>
        </div>
        <span className="rounded-full bg-white/10 px-1.5 py-0.5 text-[8px] font-black uppercase tracking-widest text-white/70">
          histórico
        </span>
      </header>
      <p className="mb-2 text-[10px] text-muted-foreground">
        {MOCK_MEDIA_BLITZ_TOTALS.clips} clips · {MOCK_MEDIA_BLITZ_TOTALS.views.toLocaleString()} vistas históricas
      </p>
      <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {clips.map((c) => (
          <div
            key={c.id}
            className="relative min-w-[160px] shrink-0 rounded-xl border border-border/60 bg-background/60 p-2.5"
          >
            <div className="absolute right-1.5 top-1.5 rounded-full bg-white/10 px-1.5 py-0.5 text-[8px] font-black uppercase tracking-widest text-white/70">
              editorial
            </div>
            <div className="line-clamp-2 text-[11px] font-black">{c.title}</div>
            <div className="mt-1 text-[9px] text-muted-foreground">
              {c.nation_code} · {c.platform}
            </div>
            <div className="mt-1 text-[9px] tabular-nums text-white/60">
              {c.views.toLocaleString()} vistas
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}